import { CloseOutlined } from '@ant-design/icons';
import {
	authState,
	ExchangeCouponPrintEventHandler,
	OrderQueryState,
	OrderState,
	PosElectronStoreState,
	PrintReceiptState,
	StoreInfoState,
} from '@kinderlabs-pos/state';
import { Button, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { WaitingIndicator } from 'apps/pos-main/src/components/PosHeader/WaitingIndicator';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrintHwasung } from '../../hwasung/usePrintHwasung';
import { DdiziPrintHandler } from '../../order/PrintHandler/DdiziPrintHandler';
import { ExchangeCouponPrintHandler } from '../../order/PrintHandler/ExchangeCouponPrintHandler';
import { JungSanDdiziResultDisplay } from './JungSanDdiziResultDisplay';
import { PointAcc } from './PointAcc';
import { VaunceDdiziPrintHandler } from '../../order/PrintHandler/VaunceDdiziPrintHandler';
import { PosBffApi } from '@kinderlabs-pos/apis/pos';
import { ReceiptChoice, ReceiptTypeSelectionDialog } from './ReceiptTypeSelectionDialog';

export interface IPaymentResultProps {
	mode: 'order_print' | 'order_not_print' | 'jungsan';
}
export const PaymentResult: React.FC<IPaymentResultProps> = ({ mode }) => {
	const { storeId, enterpriseId } = useAtomValue(StoreInfoState.curStoreAndDevice);
	const isUmmCompany = useAtomValue(authState.enterpriseInfo)?.id === 'ummcompany';
	const navigate = useNavigate();
	const order = useAtomValue(OrderState.value);

	const { isMobileReceiptAvailable } = useAtomValue(StoreInfoState.electronicReceiptConfig);
	const [dialogOpen, setDialogOpen] = useState(false);
	const 영수증수동출력 = PrintReceiptState.use영수증수동출력({ receiptType: 'ORDER' });

	const 영수증자동출력 = PrintReceiptState.use영수증자동출력();
	const kitchenSystem = StoreInfoState.useKitchenSystemInfo();

	const electronSettings = PosElectronStoreState.usePosMainPageLocalSettings();

	const 주방주문증크게 = electronSettings.data?.usingLargeTextOnKitchenReceiptPrinter ?? false;
	const 결제완료후자동으로이전페이지이동 =
		electronSettings.data?.goAutoMainPageAfterCompleteOrder ?? false;

	const { printCafe주문서Async: printCafeReceiptAsync } = PrintReceiptState.useCafePrinter({
		storeId,
		deviceType: 'POS',
	});

	const { printKitchen주문서Async: printKitchenReceiptAsync } = PrintReceiptState.useKitchenPrinter(
		{ storeId, deviceType: 'POS' }
	);

	const isDdiziComplete = OrderState.printing.ddizis.useIsPrintingComplete();
	const isExchangeCouponsComplete = OrderState.printing.exchangeCoupons.useIsPrintingComplete();

	const [프린트도끝났니, set프린트도끝났니] = useState(false);
	const printComplete = isDdiziComplete && isExchangeCouponsComplete && 프린트도끝났니;

	const resumePrinting = ExchangeCouponPrintEventHandler.useResume();

	useEffect(() => {
		(async () => {
			switch (mode) {
				case 'order_print': {
					if (!isMobileReceiptAvailable) {
						await 영수증자동출력({ order, payments: order.payments });
					}
					resumePrinting();
					if (!kitchenSystem) {
						await printCafeReceiptAsync({ order, 주방주문증크게 });
						await printKitchenReceiptAsync({ order, 주방주문증크게 });
					}
					if (isMobileReceiptAvailable) {
						setDialogOpen(true);
					} else {
						set프린트도끝났니(true);
					}
					break;
				}
				case 'jungsan': {
					await 영수증자동출력({ order, payments: order.payments });
					set프린트도끝났니(true);
					break;
				}
			}
		})();
	}, [mode, kitchenSystem, isMobileReceiptAvailable]);

	const setCalled지류티켓 = useSetAtom(WaitingIndicator.called지류티켓Atom);
	const clear = OrderState.actions.useClear();
	useEffect(() => {
		return () => {
			clear();
			setCalled지류티켓(undefined);

			if (goToPrevPageTimeOut) clearTimeout(goToPrevPageTimeOut);
		};
	}, []);

	useEffect(() => {
		if (printComplete && 결제완료후자동으로이전페이지이동) {
			// 2초후
			goToPrevPageTimeOut = setTimeout(() => {
				navigate(-1);
			}, 1000);
		}
	}, [printComplete]);

	const handleReceiptConfirm = async (choice: ReceiptChoice, phoneNumber?: string) => {
		try {
			if ((choice === '전자' || choice === '둘다') && phoneNumber) {
				await PosBffApi.postElectronicReceipt({
					orderId: order.id!,
					phoneNumber,
				});
			}
			if (choice === '일반' || choice === '둘다') {
				await 영수증수동출력({ order, payments: order.payments });
			}
		} catch (e) {
			// TODO: show error toast once BE confirms desired behavior
			console.error('영수증 처리 오류', e);
		} finally {
			setDialogOpen(false);
			set프린트도끝났니(true);
		}
	};

	const handleReceiptDialogClose = () => {
		setDialogOpen(false);
		set프린트도끝났니(true);
	};

	return (
		<Stack
			flex={1}
			spacing={1}
			height={'100%'}>
			<Typography variant={'h1'}>결제 완료</Typography>
			<Stack
				flex={1}
				spacing={1}
				overflow={'auto'}>
				<PointAcc mode={'immediate'} />
				{(enterpriseId !== 'vaunce' || order.storeId === 34) && (
					<DdiziPrintHandler StackProps={{ flex: 1, sx: { overflowY: 'auto' } }} />
				)}
				{enterpriseId === 'vaunce' && order.id && order.storeId !== 34 && (
					<VaunceDdiziPrintHandler
						orderId={order.id}
						storeId={order.storeId!}
						guestVisitId={order.guestMemberInfo?.guestVisitId}
						users={order.guestMemberInfo?.users ?? []}
						StackProps={{ flex: 1, sx: { overflowY: 'auto' } }}
					/>
				)}
				<ExchangeCouponPrintHandler StackProps={{ flex: 1, sx: { overflowY: 'auto' } }} />
			</Stack>
			{mode === 'jungsan' && <JungSanDdiziResultDisplay />}
			{isUmmCompany && order.id && <화성프린터뽑아보자 orderId={order.id} />}
			{isMobileReceiptAvailable && (
				<ReceiptTypeSelectionDialog
					open={dialogOpen}
					defaultPhoneNumber={order.guestMemberInfo?.telephone}
					onConfirm={handleReceiptConfirm}
					onClose={handleReceiptDialogClose}
				/>
			)}
			<Stack
				direction={'row'}
				spacing={1}>
				{mode === 'order_print' && (
					<Button
						sx={{ flex: 1 }}
						disabled={printComplete}
						startIcon={<CloseOutlined style={{ color: 'inherit' }} />}
						color={'error'}
						variant='outlined'
						onClick={() => navigate(-1)}>
						{'인쇄 취소 및 돌아가기'}
					</Button>
				)}
				<Button
					sx={{ flex: 1 }}
					color={'primary'}
					variant='contained'
					disabled={mode === 'order_print' && !printComplete}
					onClick={() => navigate(-1)}>
					{'이전 화면으로 돌아가기'}
				</Button>
			</Stack>
		</Stack>
	);
};

let goToPrevPageTimeOut: NodeJS.Timeout | null = null;

const 화성프린터뽑아보자 = ({ orderId }: { orderId: string }) => {
	const printHwasung = usePrintHwasung();

	const orderData = useQuery(OrderQueryState.keys.detailByOrderId(orderId));
	useEffect(() => {
		if (orderData.data) {
			printHwasung({ order: orderData.data });
		}
	}, [orderData.data]);

	return null;
};
